/**
 * Centralized timeout configuration for async operations.
 *
 * All timeouts are in MILLISECONDS (frontend) - backend has these in seconds.
 * Import these constants instead of hardcoding timeout values.
 *
 * Usage:
 *   import { TIMEOUTS } from '@/config/timeouts';
 *   const timeout = TIMEOUTS.AMADEUS_SINGLE;
 */

// =============================================================================
// AMADEUS OPERATIONS (WebSocket + REST)
// =============================================================================

/** Single cryptic command execution (e.g., RT, AN, DO) */
export const AMADEUS_SINGLE = 30_000;

/** Seat map retrieval with MD pagination (can fetch 10+ pages) */
export const AMADEUS_SEATMAP = 120_000;

/** Availability search (AN command) */
export const AMADEUS_AVAILABILITY = 30_000;

/** Amadeus session validation timeout */
export const AMADEUS_SESSION_VALIDATION = 15_000;

// =============================================================================
// AI OPERATIONS (Gemini)
// =============================================================================

/** Booking confirmation extraction (PDF/image → structured data) */
export const BOOKING_EXTRACTION = 60_000;

/** Document categorization (single AI call) */
export const DOCUMENT_CATEGORIZATION = 30_000;

/** Expense receipt extraction */
export const EXPENSE_EXTRACTION = 30_000;

// =============================================================================
// WEBSOCKET
// =============================================================================

/** WebSocket ping interval for keep-alive */
export const WEBSOCKET_PING_INTERVAL = 30_000;

/** WebSocket reconnection backoff minimum */
export const WEBSOCKET_RECONNECT_MIN = 1_000;

/** WebSocket reconnection backoff maximum */
export const WEBSOCKET_RECONNECT_MAX = 30_000;

/** Maximum reconnection attempts before giving up */
export const WEBSOCKET_MAX_RECONNECT_ATTEMPTS = 10;

// =============================================================================
// POLLING INTERVALS
// =============================================================================

/** Status indicator polling interval */
export const STATUS_POLL_INTERVAL = 30_000;

// =============================================================================
// CONVENIENCE EXPORTS
// =============================================================================

/**
 * All timeouts as a single object for easy destructuring.
 */
export const TIMEOUTS = {
  // Amadeus
  AMADEUS_SINGLE,
  AMADEUS_SEATMAP,
  AMADEUS_AVAILABILITY,
  AMADEUS_SESSION_VALIDATION,

  // AI
  BOOKING_EXTRACTION,
  DOCUMENT_CATEGORIZATION,
  EXPENSE_EXTRACTION,

  // WebSocket
  WEBSOCKET_PING_INTERVAL,
  WEBSOCKET_RECONNECT_MIN,
  WEBSOCKET_RECONNECT_MAX,
  WEBSOCKET_MAX_RECONNECT_ATTEMPTS,

  // Polling
  STATUS_POLL_INTERVAL,
} as const;

/**
 * Request-specific timeouts for Amadeus queue operations.
 * Matches backend REQUEST_TIMEOUTS structure.
 */
export const REQUEST_TIMEOUTS = {
  single: AMADEUS_SINGLE,
  seatmap: AMADEUS_SEATMAP,
  availability: AMADEUS_AVAILABILITY,
} as const;

export type RequestType = keyof typeof REQUEST_TIMEOUTS;
